import { Dialog } from 'primereact/dialog';

import { PreviewDefaultWrapper } from '@features/campaigns/builder/components/FormFields/common/CreativePreviewModal/components/PreviewDefaultWrapper/PreviewDefaultWrapper.tsx';
import { PreviewHighImpactWrapper } from '@features/campaigns/builder/components/FormFields/common/CreativePreviewModal/components/PreviewHighImpactWrapper/PreviewHighImpactWrapper.tsx';
import { PreviewSwitchInWrapper } from '@features/campaigns/builder/components/FormFields/common/CreativePreviewModal/components/PreviewSwitchInWrapper/PreviewSwitchInWrapper.tsx';
import { PreviewTVSimpleWrapper } from '@features/campaigns/builder/components/FormFields/common/CreativePreviewModal/components/PreviewTVSimpleWrapper/PreviewTVSimpleWrapper.tsx';
import { PreviewTVWrapper } from '@features/campaigns/builder/components/FormFields/common/CreativePreviewModal/components/PreviewTVWrapper/PreviewTVWrapper.tsx';
import { PreviewVideoWrapper } from '@features/campaigns/builder/components/FormFields/common/CreativePreviewModal/components/PreviewVideoWrapper/PreviewVideoWrapper.tsx';
import {
  PreviewCreativeModalOptions,
  SwitchInPreviewModalOptions,
} from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { CREATIVE_TYPE, CreativeType } from '@features/campaigns/builder/types/creativeType.ts';
import { HighImpactCreativeType } from '@features/campaigns/shared/types/campaign.ts';

interface CreativePreviewAssetsConfiguration {
  [CREATIVE_TYPE.DEFAULT]: {
    creativeUrl: string;
  };
  [CREATIVE_TYPE.SWITCH_IN]: {
    leftCreativeUrl?: string;
    bottomCreativeUrl?: string;
  };
}

interface CreativePreviewModal {
  className?: string;
  isModalVisible: boolean;
  toggleModalVisibility: () => void;
  creativeType: CreativeType;
  creativeUrl?: string;
  headerText: string;
  editablePreview?: boolean;
  previewModalOptions?: PreviewCreativeModalOptions;
  switchInAssets?: CreativePreviewAssetsConfiguration[typeof CREATIVE_TYPE.SWITCH_IN];
  isLeftPreviewEditable?: boolean;
  isBottomPreviewEditable?: boolean;
  switchInPreviewModalOptions?: SwitchInPreviewModalOptions;
  highImpactPreviewOptions?: HighImpactPreviewOptions;
}

interface HighImpactPreviewOptions {
  formatName: string;
  highImpactCreativeType: HighImpactCreativeType;
}

/* 
  The `Dialog` implementation used in this component is customized! 
  In order to enable faster loading of images,
  we had to set `visible` prop to `true`,
  hide the `Dialog` using `display: none` in the styles (`hidden` class),
  to avoid displaying the background of the `Dialog` the `modal` prop is dependent on `isModalVisible`
*/
export function CreativePreviewModal({
  isModalVisible,
  toggleModalVisibility,
  creativeType = CREATIVE_TYPE.DEFAULT,
  creativeUrl = '',
  headerText,
  editablePreview,
  previewModalOptions,
  switchInAssets,
  isLeftPreviewEditable = false,
  isBottomPreviewEditable = false,
  switchInPreviewModalOptions,
  highImpactPreviewOptions,
}: CreativePreviewModal) {
  const isDefaultCreative = creativeType === CREATIVE_TYPE.DEFAULT;
  const isLannerCreative = creativeType === CREATIVE_TYPE.LSHAPE;
  const isLandingCreative = creativeType === CREATIVE_TYPE.LANDING;
  const isSwitchInCreative = creativeType === CREATIVE_TYPE.SWITCH_IN;
  const isVideoCreative = creativeType === CREATIVE_TYPE.VIDEO;
  const isHighImpactCreative = creativeType === CREATIVE_TYPE.HIGH_IMPACT;

  return (
    <Dialog
      visible={isModalVisible}
      onHide={toggleModalVisibility}
      showHeader
      header={headerText}
      draggable={false}
      resizable={false}
      modal={isModalVisible}
    >
      {isDefaultCreative && <PreviewDefaultWrapper imageUrl={creativeUrl} />}
      {isLannerCreative && (
        <PreviewTVWrapper imageUrl={creativeUrl} editablePreview={editablePreview} options={previewModalOptions} />
      )}
      {isSwitchInCreative && (
        <PreviewSwitchInWrapper
          leftCreativeUrl={switchInAssets?.leftCreativeUrl}
          bottomCreativeUrl={switchInAssets?.bottomCreativeUrl}
          isLeftPreviewEditable={isLeftPreviewEditable}
          isBottomPreviewEditable={isBottomPreviewEditable}
          options={switchInPreviewModalOptions}
        />
      )}
      {isLandingCreative && (
        <PreviewTVSimpleWrapper
          imageUrl={creativeUrl}
          editablePreview={editablePreview}
          options={previewModalOptions}
        />
      )}
      {isVideoCreative && (
        <PreviewVideoWrapper fileUrl={creativeUrl} editablePreview={editablePreview} options={previewModalOptions} />
      )}
      {isHighImpactCreative && highImpactPreviewOptions && (
        <PreviewHighImpactWrapper
          imageUrl={creativeUrl}
          formatName={highImpactPreviewOptions.formatName}
          highImpactCreativeType={highImpactPreviewOptions.highImpactCreativeType}
          editablePreview={editablePreview}
          options={previewModalOptions}
        />
      )}
    </Dialog>
  );
}
